import { z } from 'zod';

/**
 * Shared username validator: 3–32 chars, letters/digits/underscore only.
 * Gated by `tenant_config.features.require_username_on_signup`; uniqueness is
 * enforced case-insensitively at the route (the column is citext).
 */
export const usernameSchema = z
  .string()
  .min(3)
  .max(32)
  .regex(/^[a-zA-Z0-9_]+$/, 'username_invalid_chars');

export const loginSchema = z
  .object({
    // Either `email` (admin/cashier, and members by default) or `identifier`
    // (member SPA — an email OR username when `allow_login_by_username`). The
    // route resolves `identifier ?? email`. Both kept optional for backward
    // compat with existing callers that only send `email`.
    email: z.string().email().optional(),
    identifier: z.string().min(3).max(254).optional(),
    password: z.string().min(8).max(200),
    totp: z
      .string()
      .regex(/^\d{6}$/)
      .optional(),
  })
  .refine((d) => Boolean(d.email || d.identifier), {
    path: ['email'],
    message: 'email_or_username_required',
  });
export type LoginInput = z.infer<typeof loginSchema>;

/**
 * Bringing a deactivated account back. Same credential shape as a login,
 * because reactivating IS proving you are the account holder — but no TOTP:
 * the second factor is checked on the sign-in that follows, and asking for it
 * here would strand anyone whose authenticator moved on with their old phone.
 */
export const reactivateAccountSchema = z.object({
  identifier: z.string().min(3).max(254),
  password: z.string().min(8).max(200),
});
export type ReactivateAccountInput = z.infer<typeof reactivateAccountSchema>;

/**
 * Member self-signup. Phase 3 required a sponsor code; Phase 10 made it
 * optional — when absent, the route falls back to super_admin's referral_code
 * (the replicated-link / direct-registration UX where members don't always
 * arrive with a known sponsor).
 */
export const signupSchema = z
  .object({
    email: z.string().email(),
    password: z.string().min(8).max(200),
    confirmPassword: z.string().min(8).max(200),
    firstName: z.string().min(1).max(100),
    middleName: z.string().max(100).optional(),
    lastName: z.string().min(1).max(100),
    phone: z.string().optional(),
    sponsorCode: z.string().min(1).max(64).optional(),
    // Per-package replicated link: the KIT PRODUCT id the prospect arrived
    // through (`/signup?ref=…&pkg=<kitProductId>`). Validated at the route as an
    // ACTIVE kit; scopes the new lead's dashboard kit offering to just that
    // package. Optional — a plain link carries no `pkg`.
    pkg: z.string().uuid().optional(),
    // Replicated CAMPAIGN slug the prospect arrived through
    // (`/r/<ref>/<slug>` forwards it as `?c=<slug>`). Resolved at the route to a
    // `replicated_pages.id` and stored on the new user; a tagged campaign
    // narrows which activation kits the lead may buy. Optional — a bare link or
    // the default campaign carries none. Loosely bounded; unknown slugs resolve
    // to null (tier-only scoping) rather than erroring.
    campaignSlug: z.string().min(1).max(120).optional(),
    // Optional in the schema; the route requires + uniqueness-checks it only
    // when `require_username_on_signup` is on.
    username: usernameSchema.optional(),
    // BF1: terms acceptance. Optional in the schema so existing callers keep
    // working; the route enforces it only when `require_terms_on_signup` is on.
    // `termsVersion` is the version the client believes it's accepting and is
    // validated against the current tenant version at the route.
    termsAccepted: z.boolean().optional(),
    termsVersion: z.number().int().positive().optional(),
    // Admin-defined extra signup fields, keyed by field id. A text field's value
    // is a string; a file field's value is the uploaded `{ r2Key, filename }`
    // from POST /auth/registration-upload. The route validates required fields +
    // shapes against the tenant's `registration_fields` config.
    registrationData: z
      .record(
        z.union([
          z.string().max(2000),
          z.object({ r2Key: z.string().min(1).max(512), filename: z.string().min(1).max(255) }),
        ]),
      )
      .optional(),
  })
  .refine((d) => d.password === d.confirmPassword, {
    path: ['confirmPassword'],
    message: 'passwords_do_not_match',
  });
export type SignupInput = z.infer<typeof signupSchema>;

/**
 * Admin creates a user. Phase 2 used this only for staff/admin/cashier
 * (roleCodes required). Phase 13.5 widened it: `roleCodes` is now optional
 * so the admin can create a plain member account without any role; also
 * accepts `sponsorCode` + `status` to mirror what's possible at signup.
 */
export const adminCreateUserSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8).max(200),
  firstName: z.string().min(1).max(100),
  middleName: z.string().max(100).optional(),
  lastName: z.string().min(1).max(100),
  phone: z.string().optional(),
  sponsorCode: z.string().min(1).max(64).optional(),
  username: usernameSchema.optional(),
  status: z.enum(['active', 'pending', 'suspended']).optional(),
  roleCodes: z
    // QoL Slice 5b — `manager` joins the closed-set role enum so the admin
    // create-user form can spin up a discount-override approver in one step.
    .array(z.enum(['super_admin', 'admin', 'cashier', 'manager', 'member']))
    .optional(),
  scopeBranchId: z.string().uuid().nullable().optional(),
});
export type AdminCreateUserInput = z.infer<typeof adminCreateUserSchema>;

export const refreshSchema = z.object({}).strict();

export const totpEnrollSchema = z.object({
  password: z.string().min(8),
});

export const totpVerifySchema = z.object({
  code: z.string().regex(/^\d{6}$/),
});
